import React, { useState, useEffect, useCallback } from 'react'
import {
  Table,
  Breadcrumb,
  InputGroup,
  FormControl,
  Button,
  Container,
  Row,
  Col,
} from 'react-bootstrap'
import { useHistory, useLocation } from 'react-router'
import qs from 'query-string'
import useOrganizationsSearch from 'hooks/use-organizations-search'
import useRecentOrganizationSearches from 'hooks/use-recent-organization-searches'
import useRecentOrganizations from 'hooks/use-recent-organizations'
import OrganizationsSidebar from 'components/organizations-sidebar'
import { Organization } from 'interfaces/organization'
import { formatDateString } from 'utils/date-utils'

const OrganizationsPage = () => {
  const history = useHistory()
  const location = useLocation()
  const params = qs.parse(location.search)
  const [query, setQuery] = useState(params.query || '')
  const { data, isLoading } = useOrganizationsSearch({
    query: params.query || '',
  })
  const { addRecentSearch } = useRecentOrganizationSearches()
  const { addRecentOrganization } = useRecentOrganizations()

  useEffect(() => {
    if (
      params.query &&
      typeof params.query === 'string' &&
      params.query.trim()
    ) {
      addRecentSearch(params.query.trim())
    }
  }, [params.query, addRecentSearch])

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setQuery(event.target.value)
  }

  const handleOrganizationClick = (org: Organization) => {
    addRecentOrganization({
      id: org.id,
      name: org.name,
    })
    history.push(`/organizations/${org.id}`)
  }

  const handleSubmit = () => {
    if (query) {
      history.push(`/organizations?query=${encodeURIComponent(query)}`)
    } else {
      history.push('/organizations')
    }
  }

  return (
    <Container fluid className="mt-5 pt-5 px-4">
      <Row>
        <Col lg={9}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item active href="/organizations">
              Organizations
            </Breadcrumb.Item>
          </Breadcrumb>
          <div className="d-flex justify-content-between align-items-center mt-4 mb-4">
            <h3 className="mb-0">Organizations</h3>
            <Button
              variant="success"
              onClick={() => history.push('/organizations/new')}
            >
              + New Organization
            </Button>
          </div>
          <div className="position-relative mb-4 mt-4">
            <InputGroup>
              <FormControl
                autoComplete="off"
                onChange={handleSearchInputChange}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.code === 'Enter' || e.code === 'NumpadEnter') {
                    handleSubmit()
                  }
                }}
                value={query}
                type="text"
                placeholder="Search"
                aria-label="Search Organizations"
                autoFocus
              />
              <Button variant="success" onClick={handleSubmit}>
                <ion-icon name="search" />
              </Button>
            </InputGroup>
          </div>
          {isLoading ? (
            <div className="m-2">Searching...</div>
          ) : (
            <Table striped bordered hover responsive variant="dark">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Logo</th>
                  <th>Org Name</th>
                  <th>Account Manager</th>
                  <th>User Count</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                {data?.organizations?.map((org) => (
                  <tr
                    key={org.id}
                    className="pointer"
                    onClick={() => handleOrganizationClick(org)}
                  >
                    <td style={{ width: '100px' }}>{org.id}</td>
                    <td style={{ width: '100px' }}>
                      {org.badge_logo_url && (
                        <img
                          src={org.badge_logo_url}
                          alt="org logo"
                          className="w-100"
                        />
                      )}
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        {org.name}
                        {new Date(org.created_at).toDateString() ===
                          new Date().toDateString() && (
                          <span
                            className="fw-bold ms-2"
                            style={{
                              fontSize: '0.75rem',
                              letterSpacing: '0.5px',
                              background:
                                'linear-gradient(45deg, #8b5cf6, #06b6d4, #fbbf24)',
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              color: 'transparent',
                              textShadow: 'none',
                            }}
                          >
                            ✨ New Org
                          </span>
                        )}
                        {org.subscription_status && (
                          <span
                            className={`badge ${
                              org.subscription_status === 'active'
                                ? 'bg-success'
                                : org.subscription_status === 'trialing'
                                ? 'bg-info'
                                : org.subscription_status === 'past_due' ||
                                  org.subscription_status === 'canceled' ||
                                  org.subscription_status === 'unpaid' ||
                                  org.subscription_status === 'trial_expired'
                                ? 'bg-danger'
                                : 'bg-danger'
                            }`}
                          >
                            {org.subscription_status}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{org.account_manager_name}</td>
                    <td>{org.user_count}</td>
                    <td>{formatDateString(org.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Col>
        <Col lg={3}>
          <div className="sticky-top" style={{ top: '120px' }}>
            <OrganizationsSidebar />
          </div>
        </Col>
      </Row>
    </Container>
  )
}

export default OrganizationsPage
